import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Upload,
  FileText,
  Eye,
  Download,
  Trash2,
  Search,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader,
  FolderOpen,
  List,
  Network,
  Building2,
} from "lucide-react";
import {
  useGetDocumentsQuery,
  useSearchDocumentsQuery,
  useGetSearchSuggestionsQuery,
  useDeleteDocumentMutation,
  useBulkDeleteDocumentsMutation,
  useGetVendorsQuery,
  useGetDocumentRelationshipsQuery,
} from "../store/api/apiSlice";
import { useSelector } from "react-redux";
import { MultiStepDocumentUpload } from "../components/ui/MultiStepDocumentUpload";
import { BulkDocumentUpload } from "../components/ui/BulkDocumentUpload";
import { LoadingSpinner } from "../components/ui/LoadingSpinner";
import { DocumentRelationshipDiagram } from "../components/ui/DocumentRelationshipDiagram";
import { AdvancedSearchInput } from "../components/ui/AdvancedSearchInput";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

type ViewMode = "list" | "diagram";

export const DocumentsPage: React.FC = () => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [selectedDocumentId, setSelectedDocumentId] = useState<
    string | undefined
  >();
  const [currentPage, setCurrentPage] = useState(1);
  const [shouldPoll, setShouldPoll] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  // Get auth state to determine when API calls should be made
  const auth = useSelector((state: any) => state.auth);
  const isAuthReady = auth.isAuthenticated && !auth.isLoading && auth.token;

  const {
    data: documentsData,
    isLoading,
    error,
    refetch,
  } = useGetDocumentsQuery(
    {
      skip: (currentPage - 1) * itemsPerPage,
      limit: itemsPerPage,
      status_filter: statusFilter || undefined,
      document_type: typeFilter || undefined,
    },
    {
      // Skip API call until authentication is ready or if in search mode
      skip: !isAuthReady || isSearchMode,
      // Poll every 5 seconds to get status updates when shouldPoll is true
      pollingInterval: shouldPoll ? 5000 : 0,
    }
  );

  // Search query for advanced search
  const {
    data: searchData,
    isLoading: isSearchLoading,
    error: searchError,
    refetch: refetchSearch,
  } = useSearchDocumentsQuery(
    {
      query: searchTerm,
      skip: (currentPage - 1) * itemsPerPage,
      limit: itemsPerPage,
      vendor_id: vendorFilter || undefined,
      status_filter: statusFilter || undefined,
      document_type: typeFilter || undefined,
    },
    {
      // Skip API call until authentication is ready or if not in search mode
      skip: !isAuthReady || !isSearchMode || !searchTerm.trim(),
      // Poll every 5 seconds to get status updates when shouldPoll is true
      pollingInterval: shouldPoll ? 5000 : 0,
    }
  );

  // Get vendors for filtering
  const { data: vendorsData, isLoading: isLoadingVendors } = useGetVendorsQuery(
    {
      limit: 100,
      is_active: true,
    },
    {
      skip: !isAuthReady,
    }
  );

  // Get ALL documents for diagram view (not paginated)
  const { data: allDocumentsData, isLoading: isLoadingAllDocuments } =
    useGetDocumentsQuery(
      {
        skip: 0,
        limit: 100, // Get all documents for diagram
        status_filter: statusFilter || undefined,
        document_type: typeFilter || undefined,
      },
      {
        skip: !isAuthReady || viewMode !== "diagram",
      }
    );

  // Get relationships for diagram view
  const { data: relationshipsData, isLoading: isLoadingRelationships } =
    useGetDocumentRelationshipsQuery(
      {
        limit: 100, // Maximum allowed limit by API
      },
      {
        skip: !isAuthReady || viewMode !== "diagram",
      }
    );

  // Get search suggestions for live autocomplete (only when typing without tokens)
  const shouldGetSuggestions = searchTerm.trim() && 
    !searchTerm.includes(':') && 
    !searchTerm.includes('~') && 
    searchTerm.length >= 2;
  
  const { data: suggestionsData } = useGetSearchSuggestionsQuery(
    {
      query: searchTerm,
      limit: 5,
    },
    {
      skip: !isAuthReady || !shouldGetSuggestions,
    }
  );

  const [deleteDocument] = useDeleteDocumentMutation();
  const [bulkDeleteDocuments] = useBulkDeleteDocumentsMutation();

  // Control polling based on document states
  React.useEffect(() => {
    if (documentsData?.items) {
      const hasProcessingDocuments = documentsData.items.some(
        (doc) =>
          doc.processing_status === "pending" ||
          doc.processing_status === "processing"
      );
      setShouldPoll(hasProcessingDocuments);
    }
  }, [documentsData?.items]);

  const handleDeleteDocument = async (documentId: string) => {
    if (window.confirm("Are you sure you want to delete this document?")) {
      try {
        await deleteDocument(documentId).unwrap();
        toast.success("Document deleted successfully");
        currentRefetch();
      } catch (error: any) {
        toast.error(error?.data?.detail || "Failed to delete document");
      }
    }
  };

  const toggleSelect = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const set = new Set(prev);
      if (checked) set.add(id);
      else set.delete(id);
      return Array.from(set);
    });
  };

  const toggleSelectAllOnPage = (checked: boolean) => {
    const pageIds = filteredDocuments.map((d) => d.id);
    setSelectedIds((prev) => {
      const set = new Set(prev);
      if (checked) pageIds.forEach((id) => set.add(id));
      else pageIds.forEach((id) => set.delete(id));
      return Array.from(set);
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected document(s)?`)) return;
    try {
      const res = await bulkDeleteDocuments({ ids: selectedIds }).unwrap();
      const deleted = res.deleted_ids?.length || 0;
      const failed = res.failed_ids?.length || 0;
      if (deleted > 0) toast.success(`Deleted ${deleted} document(s)`);
      if (failed > 0) toast.error(`Failed to delete ${failed} document(s)`);
      setSelectedIds((prev) => prev.filter((id) => !res.deleted_ids.includes(id)));
      currentRefetch();
    } catch (error: any) {
      toast.error(error?.data?.detail || "Bulk delete failed");
    }
  };

  const handleUploadComplete = (document: any) => {
    // Immediately refetch to show the new document
    currentRefetch();
    // Start polling to track the document processing status
    setShouldPoll(true);
    toast.success("Document uploaded successfully!");
  };

  const handleBulkUploadComplete = (documents: any[]) => {
    // Immediately refetch to show the new documents
    currentRefetch();
    // Start polling to track the document processing status
    setShouldPoll(true);
    toast.success(`${documents.length} documents uploaded successfully!`);
  };

  const handleSearch = useCallback((query: string) => {
    setSearchTerm(query);
    setIsSearchMode(!!query.trim());
    setCurrentPage(1); // Reset to first page on new search
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchTerm(query);
    if (!query.trim()) {
      setIsSearchMode(false);
    }
  }, []);

  const handleSelectSearchResult = useCallback((result: { id: string; title: string; original_filename: string }) => {
    // Navigate to the selected document
    navigate(`/documents/${result.id}`);
  }, [navigate]);

  const handleDocumentClick = useCallback((document: any) => {
    setSelectedDocumentId(document.id);
    // You could also navigate to document detail page
    // navigate(`/documents/${document.id}`);
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case "processing":
        return <Loader className="w-4 h-4 animate-spin text-blue-500" />;
      case "completed":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "failed":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "processing":
        return "bg-blue-100 text-blue-800 border-blue-300";
      case "completed":
        return "bg-green-100 text-green-800 border-green-300";
      case "failed":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes) return "Unknown";
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get the current data source (search results or regular documents)
  const currentData = isSearchMode ? searchData : documentsData;
  const currentLoading = isSearchMode ? isSearchLoading : isLoading;
  const currentError = isSearchMode ? searchError : error;
  const currentRefetch = isSearchMode ? refetchSearch : refetch;

  // For search mode, convert search results to document format
  const currentDocuments = isSearchMode 
    ? (searchData?.results?.map(result => result.document) || [])
    : (documentsData?.items || []);

  // Filter documents based on vendor (search already handles other criteria)
  const filteredDocuments = isSearchMode 
    ? currentDocuments // Search API handles all filtering
    : currentDocuments.filter((doc) => {
        const matchesVendor = !vendorFilter || doc.vendor_id === vendorFilter;
        return matchesVendor;
      });

  // Filter ALL documents for diagram view (not paginated)
  const allFilteredDocuments =
    allDocumentsData?.items.filter((doc) => {
      const matchesSearch =
        doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.original_filename.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesVendor = !vendorFilter || doc.vendor_id === vendorFilter;

      return matchesSearch && matchesVendor;
    }) || [];

  // Get documents for diagram view (all documents, not just current page)
  const diagramDocuments = vendorFilter
    ? allFilteredDocuments.filter((doc) => doc.vendor_id === vendorFilter)
    : allFilteredDocuments;

  // Get documents for list view (paginated)
  const vendorDocuments = vendorFilter
    ? filteredDocuments.filter((doc) => doc.vendor_id === vendorFilter)
    : filteredDocuments;

  // Get relationships for diagram documents (all documents)
  const relevantRelationships =
    relationshipsData?.items.filter((rel) => {
      const parentInFilter = diagramDocuments.some(
        (doc) => doc.id === rel.parent_document_id
      );
      const childInFilter = diagramDocuments.some(
        (doc) => doc.id === rel.child_document_id
      );
      return parentInFilter || childInFilter;
    }) || [];

  if (currentError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Error loading documents
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            There was an issue loading your documents.
          </p>
          <button onClick={() => currentRefetch()} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Documents
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Upload and manage legal documents with AI-powered analysis
          </p>
        </div>
        <div className="flex space-x-3">
          {selectedIds.length > 0 && (
            <button onClick={handleBulkDelete} className="btn-danger">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Selected ({selectedIds.length})
            </button>
          )}
          <button
            onClick={async () => {
              try {
                let bearer = auth?.token as string | undefined;
                if (!bearer) {
                  const tokenKey = Object.keys(localStorage).find((k) => k.startsWith('@@auth0spajs@@'));
                  const tokenEntry = tokenKey ? localStorage.getItem(tokenKey) : null;
                  if (tokenEntry) {
                    try {
                      const parsed = JSON.parse(tokenEntry as string);
                      const anyParsed: any = parsed;
                      const accessToken = anyParsed?.body?.access_token;
                      if (accessToken) bearer = accessToken;
                    } catch {}
                  }
                }
                const params = new URLSearchParams();
                if (vendorFilter) params.set('vendor_id', vendorFilter);
                if (statusFilter) params.set('status_filter', statusFilter);
                if (typeFilter) params.set('document_type', typeFilter);
                const res = await fetch(`/api/v1/documents/export?${params.toString()}`, {
                  headers: bearer ? { Authorization: `Bearer ${bearer}` } : {},
                });
                if (!res.ok) throw new Error('Export failed');
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = window.document.createElement('a');
                a.href = url;
                a.download = 'contracts_export.xlsx';
                window.document.body.appendChild(a);
                a.click();
                window.document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
              } catch (e) {
                toast.error('Failed to export');
              }
            }}
            className="btn-outline"
          >
            Export
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="btn-primary"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Document
          </button>
          <button
            onClick={() => setShowBulkUploadModal(true)}
            className="btn-secondary"
          >
            <FolderOpen className="w-4 h-4 mr-2" />
            Bulk Upload
          </button>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="card">
        <div className="space-y-4">
          {/* Search and View Toggle */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex-1">
              <AdvancedSearchInput
                value={searchTerm}
                onChange={handleSearchChange}
                onSearch={handleSearch}
                placeholder="Search documents"
                disabled={currentLoading}
                searchResults={suggestionsData?.suggestions || []}
                onSelectResult={handleSelectSearchResult}
              />
            </div>

            {/* View Mode Toggle */}
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode("list")}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "list"
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                }`}
              >
                <List className="w-4 h-4 mr-2" />
                List View
              </button>
              <button
                onClick={() => setViewMode("diagram")}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "diagram"
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                }`}
              >
                <Network className="w-4 h-4 mr-2" />
                Diagram View
              </button>
            </div>
          </div>

          {/* Filters Row */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Vendor Filter */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Building2 className="w-4 h-4 inline mr-1" />
                Vendor
              </label>
              <select
                value={vendorFilter}
                onChange={(e) => setVendorFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                disabled={isLoadingVendors}
              >
                <option value="">All Vendors</option>
                {vendorsData?.items.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.name}{" "}
                    {vendor.vendor_type ? `(${vendor.vendor_type})` : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            {/* Type Filter */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Type
              </label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="">All Types</option>
                <option value="contract">Contract</option>
                <option value="agreement">Agreement</option>
                <option value="memo">Memo</option>
                <option value="policy">Policy</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {/* Selected Vendor Info */}
          {vendorFilter && vendorsData?.items && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <div className="flex items-center space-x-3">
                <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <div>
                  {(() => {
                    const selectedVendor = vendorsData.items.find(
                      (v) => v.id === vendorFilter
                    );
                    return selectedVendor ? (
                      <div>
                        <p className="font-medium text-blue-900 dark:text-blue-100">
                          {selectedVendor.name}
                        </p>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          {viewMode === "diagram"
                            ? diagramDocuments.length
                            : vendorDocuments.length}{" "}
                          {(viewMode === "diagram"
                            ? diagramDocuments.length
                            : vendorDocuments.length) === 1
                            ? "document"
                            : "documents"}
                          {selectedVendor.vendor_type &&
                            ` • ${selectedVendor.vendor_type}`}
                        </p>
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content Area */}
      {viewMode === "list" ? (
        /* Documents List */
        <div className="card">
          {currentLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                {currentDocuments.length === 0
                  ? "No documents yet"
                  : isSearchMode 
                    ? "No documents match your search"
                    : "No documents match your filters"}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {currentDocuments.length === 0
                  ? "Upload your first document to get started with AI-powered analysis."
                  : isSearchMode
                    ? "Try different search terms or check the search syntax help."
                    : "Try adjusting your search criteria or filters."}
              </p>
              {currentDocuments.length === 0 && (
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="btn-primary"
                >
                  Upload Your First Document
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="w-8 px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        aria-label="Select all on page"
                        className="w-4 h-4"
                        checked={filteredDocuments.length > 0 && filteredDocuments.every((d) => selectedIds.includes(d.id))}
                        onChange={(e) => toggleSelectAllOnPage(e.target.checked)}
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Document
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Vendor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Uploaded
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredDocuments.map((document) => (
                    <tr
                      key={document.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <td className="w-8 px-2 py-2 text-center whitespace-nowrap">
                        <input
                          type="checkbox"
                          aria-label={`Select document ${document.original_filename}`}
                          className="w-4 h-4"
                          checked={selectedIds.includes(document.id)}
                          onChange={(e) => toggleSelect(document.id, e.target.checked)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/documents/${document.id}`} className="flex items-center group">
                          <FileText className="w-5 h-5 text-gray-400 mr-3 group-hover:text-primary-600" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate group-hover:text-primary-600">
                              {document.title || document.original_filename}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                              {document.original_filename}
                            </p>
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          {!document.document_type &&
                            !document.contract_type && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                                Unknown
                              </span>
                            )}
                          {document.document_type && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                              {document.document_type}
                            </span>
                          )}
                          {document.contract_type && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-300">
                              {document.contract_type}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {document.vendor ? (
                          <div className="text-sm">
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {document.vendor.name}
                            </p>
                            {document.vendor.vendor_type && (
                              <p className="text-gray-500 dark:text-gray-400">
                                {document.vendor.vendor_type}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">
                            No vendor
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(
                            document.processing_status
                          )}`}
                        >
                          {getStatusIcon(document.processing_status)}
                          <span className="ml-1 capitalize">
                            {document.processing_status}
                          </span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatFileSize(document.file_size || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(document.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Link
                            to={`/documents/${document.id}`}
                            className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                            title="View details"
                          >
                            <Eye className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={async () => {
                              try {
                                const tokenKey = Object.keys(localStorage).find(k => k.startsWith('@@auth0spajs@@')); 
                                const tokenEntry = tokenKey ? localStorage.getItem(tokenKey) : null;
                                let bearer = '';
                                if (tokenEntry) {
                                  try {
                                    const parsed = JSON.parse(tokenEntry as string);
                                    const anyParsed: any = parsed;
                                    const accessToken = anyParsed?.body?.access_token;
                                    if (accessToken) bearer = accessToken;
                                  } catch {}
                                }
                                const res = await fetch(`/api/v1/documents/${document.id}/download`, {
                                  headers: bearer ? { Authorization: `Bearer ${bearer}`, Accept: 'application/pdf' } : { Accept: 'application/pdf' },
                                });
                                if (!res.ok) throw new Error('Download failed');
                                const blob = await res.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = window.document.createElement('a');
                                a.href = url;
                                a.download = document.original_filename;
                                window.document.body.appendChild(a);
                                a.click();
                                window.document.body.removeChild(a);
                                window.URL.revokeObjectURL(url);
                              } catch {
                                window.open(`/api/v1/documents/${document.id}/download`, '_blank');
                              }
                            }}
                            className="text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteDocument(document.id)}
                            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {currentData && currentData.total > itemsPerPage && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Showing{" "}
                  <span className="font-medium">
                    {(currentPage - 1) * itemsPerPage + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, currentData.total)}
                  </span>{" "}
                  of <span className="font-medium">{currentData.total}</span>{" "}
                  results
                  {isSearchMode && (
                    <span className="ml-2 text-primary-600 dark:text-primary-400">
                      (search results)
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
                  Page {currentPage}
                </span>
                <button
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                  disabled={!currentData.has_next}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      ) : vendorFilter ? (
        /* Diagram View */
        <div className="card p-6">
          {isLoadingAllDocuments || isLoadingRelationships ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">
                Loading {isLoadingAllDocuments ? "documents" : "relationships"}
                ...
              </span>
            </div>
          ) : (
            <DocumentRelationshipDiagram
              key={`diagram-${vendorFilter || 'all'}-${diagramDocuments.length}-${relevantRelationships.length}`}
              documents={diagramDocuments}
              relationships={relevantRelationships}
              selectedDocumentId={selectedDocumentId}
              onDocumentClick={handleDocumentClick}
              className="mx-auto"
            />
          )}
        </div>
      ) : (
        <div className="card p-12 text-center text-gray-600 dark:text-gray-400">
          Select a vendor to view the relationship diagram.
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg max-w-7xl max-h-[90vh] overflow-y-auto">
            <MultiStepDocumentUpload
              onUploadComplete={handleUploadComplete}
              onClose={() => setShowUploadModal(false)}
            />
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg max-w-7xl max-h-[90vh] overflow-y-auto">
            <BulkDocumentUpload
              onUploadComplete={handleBulkUploadComplete}
              onClose={() => setShowBulkUploadModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};
